import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { zodResolver } from '@hookform/resolvers/zod';
import { LogIn, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import { Alert, AlertDescription, AlertCircle } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { NewPasswordDialog } from '../components/auth/NewPasswordDialog';
import { TenantSelector } from '../components/auth/TenantSelector';
import * as cognitoService from '../lib/auth/cognito.service';
import { getMyUser, getMyTenants } from '../lib/api/user.client';
import { useAuthStore } from '../stores/auth.store';
/**
 * Login form validation schema
 */
const loginSchema = z.object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
});
export default function Login() {
    const navigate = useNavigate();
    const { setUser, setTokens, setLoading, setError, isAuthenticated, error } = useAuthStore();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [configError, setConfigError] = useState(null);
    const [passwordChallenge, setPasswordChallenge] = useState(null);
    const [showTenantSelector, setShowTenantSelector] = useState(false);
    const [availableTenants, setAvailableTenants] = useState([]);
    const [pendingTokens, setPendingTokens] = useState(null);
    const [pendingUserData, setPendingUserData] = useState(null);
    const { register, handleSubmit, formState: { errors }, } = useForm({
        resolver: zodResolver(loginSchema),
        defaultValues: {
            email: '',
            password: '',
        },
    });
    // Check Cognito configuration on mount
    useEffect(() => {
        const config = cognitoService.getCognitoConfig();
        if (!config.isConfigured) {
            setConfigError('Cognito is not configured. Please set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID environment variables.');
        }
    }, []);
    // Redirect if already authenticated
    useEffect(() => {
        if (isAuthenticated) {
            navigate('/', { replace: true });
        }
    }, [isAuthenticated, navigate]);
    const completeLogin = async (tokens, userData, selectedTenantId, companyName) => {
        try {
            // Store tokens with selected tenant context
            setTokens(tokens);
            // Store selected tenant in localStorage for API calls
            localStorage.setItem('selectedTenantId', selectedTenantId);
            // Update user data with selected tenant
            const userWithTenant = {
                ...userData,
                tenantId: selectedTenantId,
                companyName,
            };
            // Fetch fresh user profile for the selected tenant
            try {
                const userProfile = await getMyUser();
                setUser({
                    ...userWithTenant,
                    roles: userProfile.roles,
                    ...(userProfile.employeeId && { employeeId: userProfile.employeeId }),
                });
                await new Promise(resolve => setTimeout(resolve, 50));
            }
            catch (profileErr) {
                console.error('Failed to fetch user profile, using token data:', profileErr);
                setUser(userWithTenant);
            }
            navigate('/', { replace: true });
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to complete login';
            setError(errorMessage);
        }
        finally {
            setIsSubmitting(false);
            setLoading(false);
        }
    };
    const onSubmit = async (data) => {
        setIsSubmitting(true);
        setLoading(true);
        setError(null);
        try {
            const result = await cognitoService.login({
                email: data.email,
                password: data.password,
            });
            // Store tokens temporarily (needed for tenant query)
            setTokens(result.tokens);
            setPendingTokens(result.tokens);
            setPendingUserData(result.user);
            // Fetch available tenants for this user
            try {
                const { tenants } = await getMyTenants();
                if (!tenants || tenants.length === 0) {
                    throw new Error('No tenants found for this user');
                }
                if (tenants.length === 1) {
                    // Auto-select single tenant
                    const firstTenant = tenants[0];
                    if (!firstTenant) {
                        throw new Error('Invalid tenant data');
                    }
                    await completeLogin(result.tokens, result.user, firstTenant.tenantId, firstTenant.companyName);
                }
                else {
                    // Show tenant selector for multiple tenants
                    setAvailableTenants(tenants);
                    setShowTenantSelector(true);
                    setIsSubmitting(false);
                    setLoading(false);
                }
            }
            catch (tenantsErr) {
                console.error('Failed to fetch tenants:', tenantsErr);
                throw new Error('Failed to load your organizations. Please try again.');
            }
        }
        catch (err) {
            // Check if this is a password change challenge
            if (err && typeof err === 'object' && 'type' in err && err.type === 'NEW_PASSWORD_REQUIRED') {
                setPasswordChallenge(err);
            }
            else {
                const errorMessage = err instanceof Error ? err.message : 'Authentication failed';
                setError(errorMessage);
            }
            setIsSubmitting(false);
            setLoading(false);
        }
    };
    const handleTenantSelection = async (tenantId) => {
        if (!pendingTokens || !pendingUserData) {
            setError('Session expired. Please log in again.');
            setShowTenantSelector(false);
            return;
        }
        // Find the selected tenant to get companyName
        const selectedTenant = availableTenants.find(t => t.tenantId === tenantId);
        setIsSubmitting(true);
        setLoading(true);
        await completeLogin(pendingTokens, pendingUserData, tenantId, selectedTenant?.companyName);
    };
    const handlePasswordChange = async (newPassword) => {
        if (!passwordChallenge) {
            throw new Error('No password challenge active');
        }
        setLoading(true);
        setError(null);
        try {
            const result = await cognitoService.completeNewPasswordChallenge(passwordChallenge, newPassword);
            // Clear challenge first so dialog closes
            setPasswordChallenge(null);
            // Show success toast immediately (will persist through navigation)
            toast.success('Password Changed Successfully', {
                description: 'You can now access your account.',
                duration: 4000,
            });
            // Store user and tokens (this will trigger navigation via ProtectedRoute)
            setUser(result.user);
            setTokens(result.tokens);
            // Navigate to dashboard
            navigate('/', { replace: true });
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to change password';
            throw new Error(errorMessage);
        }
        finally {
            setLoading(false);
        }
    };
    const handlePasswordChangeCancel = () => {
        setPasswordChallenge(null);
        setError(null);
    };
    return (_jsxs(_Fragment, { children: [passwordChallenge && (_jsx(NewPasswordDialog, { open: true, email: passwordChallenge.email, onSubmit: handlePasswordChange, onCancel: handlePasswordChangeCancel })), showTenantSelector && (_jsx("div", { className: "min-h-screen flex items-center justify-center bg-gray-50 px-4", children: _jsx("div", { className: "w-full max-w-2xl", children: _jsx(TenantSelector, { tenants: availableTenants, onSelect: handleTenantSelection }) }) })), !showTenantSelector && (_jsx("div", { className: "min-h-screen flex items-center justify-center bg-gray-50 px-4", children: _jsxs("div", { className: "w-full max-w-md space-y-8", children: [_jsxs("div", { className: "text-center", children: [_jsx("h1", { className: "text-3xl font-bold text-gray-900", children: "LALO HR/Time" }), _jsx("p", { className: "mt-2 text-sm text-gray-600", children: "Sign in to your account to continue" })] }), configError && (_jsxs(Alert, { variant: "destructive", children: [_jsx(AlertCircle, { className: "h-4 w-4" }), _jsx(AlertDescription, { children: configError })] })), _jsxs("div", { className: "bg-white py-8 px-6 shadow rounded-lg", children: [_jsxs("form", { onSubmit: handleSubmit(onSubmit), className: "space-y-6", children: [_jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "email", children: "Email address" }), _jsx(Input, { id: "email", type: "email", autoComplete: "email", placeholder: "you@example.com", ...register('email') }), errors.email && (_jsx("p", { className: "text-sm text-red-600", children: errors.email.message }))] }), _jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "password", children: "Password" }), _jsx(Input, { id: "password", type: "password", autoComplete: "current-password", placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022", ...register('password') }), errors.password && (_jsx("p", { className: "text-sm text-red-600", children: errors.password.message }))] }), error && (_jsxs(Alert, { variant: "destructive", children: [_jsx(AlertCircle, { className: "h-4 w-4" }), _jsx(AlertDescription, { children: error })] })), _jsx(Button, { type: "submit", className: "w-full", disabled: isSubmitting || !!configError, children: isSubmitting ? (_jsxs(_Fragment, { children: [_jsx(Loader2, { className: "mr-2 h-4 w-4 animate-spin" }), "Signing in..."] })) : (_jsxs(_Fragment, { children: [_jsx(LogIn, { className: "mr-2 h-4 w-4" }), "Sign in"] })) })] }), _jsx("div", { className: "mt-6 text-center text-sm", children: _jsx(Link, { to: "/forgot-password", className: "text-blue-600 hover:text-blue-500", children: "Forgot your password?" }) })] }), _jsx("div", { className: "text-center", children: _jsxs("p", { className: "text-sm text-gray-600", children: ["Don't have an account?", ' ', _jsx(Link, { to: "/signup", className: "font-medium text-indigo-600 hover:text-indigo-500", children: "Sign up for free" })] }) }), _jsxs("p", { className: "text-center text-xs text-gray-500", children: ["Protected by enterprise-grade security.", _jsx("br", {}), "Your credentials are never stored locally."] })] }) }))] }));
}
